import React, {useState, useEffect} from 'react';
import {Stack, Grid, Box, Divider, Typography} from '@mui/material';
import {t} from 'i18next';
import {Trans} from 'react-i18next';
import './ProductionsFieldType.scss';
import '../FieldTypes.scss';
import {ISelectItem, ICheckboxListItem} from '../../../../../../shared/types';
import ArrCheckboxList from '../../../../../common/arr-checkbox-list/ArrCheckboxList';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrNumericInput from '../../../../../common/arr-numeric-input/ArrNumericInput';
import ArrTextArea from '../../../../../common/arr-text-area/ArrTextArea';
import ArrToastNotification from '../../../../../common/arr-toast-notification/ArrToastNotification';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {ToastType} from '../../../../../../shared/constants';
import {Field} from '../../../../types/Field';
import {FieldTypeProps} from '../FieldTypeProps';
import {useFieldData} from '../../../../../../hooks/session-search/useSessionSearch';
import {IAdvancedSearchTabsDataResponse} from '../../../../../../api/types/sessionSearch.type';

const isOperator: ISelectItem = {
  value: 'is',
  label: t('sessionSearch.queryList.workProduct.productionsDrawer.is'),
};

const rangeOperator: ISelectItem = {
  value: 'range',
  label: t('sessionSearch.queryList.workProduct.productionsDrawer.range'),
};

const listOperator: ISelectItem = {
  value: 'list',
  label: t('sessionSearch.queryList.workProduct.productionsDrawer.list'),
};

const statusItems: ISelectItem[] = [
  {
    value: 'produced',
    label: t('sessionSearch.queryList.workProduct.productionsDrawer.produced'),
  },
  {
    value: 'selected',
    label: t('sessionSearch.queryList.workProduct.productionsDrawer.selected'),
  },
];

export interface ProductionsFieldTypeProps extends Omit<
  FieldTypeProps,
  'onCriteriaChaged'
> {
  onCriteriaChaged?: (
    productions: Field[],
    status: string,
    field: Field | null,
    operator: string | null,
    value: string[]
  ) => void;
}

const ProductionsFieldType: React.FC<ProductionsFieldTypeProps> = ({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  type,
  onCriteriaChaged,
  onValidated,
}) => {
  // Productions
  const [productions, setProductions] = useState<Field[]>([]);
  const [currentProductions, setCurrentProductions] = useState<Field[]>([]);
  const [productionCheckboxItems, setProductionCheckboxItems] = useState<
    ICheckboxListItem[]
  >([]);

  // Production Fields
  const [productionFields, setProductionFields] = useState<Field[]>([]);
  const [currentStatus, setCurrentStatus] = useState<string>('produced');
  const [currentField, setCurrentField] = useState<Field | null>(null);
  const [currentOperator, setCurrentOperator] = useState<string>('is');
  const [currentValues, setCurrentValues] = useState<string[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [statusSelectItems, setStatusSelectItems] =
    useState<ISelectItem[]>(statusItems);
  const [statusSelectedItem, setStatusSelectedItem] =
    useState<ISelectItem | null>(statusItems[0]); // default to 'Already Produced'
  const [fieldSelectItems, setFieldSelectItems] = useState<ISelectItem[]>([]);
  const [fieldSelectedItem, setFieldSelectedItem] =
    useState<ISelectItem | null>(null);
  const [operatorSelectItems, setOperatorSelectItems] = useState<ISelectItem[]>(
    []
  );
  const [operatorSelectedItem, setOperatorSelectedItem] =
    useState<ISelectItem | null>(null);
  const [textAreaValue, setTextAreaValue] = useState<string>('');
  const [startNumericValue, setStartNumericValue] = useState<number | null>();
  const [endNumericValue, setEndNumericValue] = useState<number | null>();

  const [showProductionsToast, setShowProductionsToast] = useState(true);
  const {
    data: productionData,
    isPending: productionsPending,
    isError: productionsErred,
  } = useFieldData('PRODUCTIONS');

  const [showProductionFieldsToast, setShowProductionFieldsToast] =
    useState(true);
  const {
    data: productionFieldData,
    isPending: productionFieldsPending,
    isError: productionsFieldErred,
  } = useFieldData('PRODUCTIONFIELDS');

  useEffect(() => {
    loadProductionData(productionData);
    loadProductionFieldData(productionFieldData);
  }, [productionData, productionFieldData]);

  useEffect(() => {
    // Set STATUS when one is selected from the list
    let status: string | null = null;
    if (statusSelectedItem && statusSelectedItem.value) {
      status = statusSelectedItem.value.toString();
      setCurrentStatus(status);
    }
  }, [statusSelectedItem]);

  useEffect(() => {
    // Set FIELD when one is selected from the list
    let field: Field | null = null;
    if (fieldSelectedItem) {
      field =
        productionFields.find(
          (value) => value.id === fieldSelectedItem.value
        ) ?? null;
      setCurrentField(field);
      setOperators(field);

      setTextAreaValue('');
      setStartNumericValue(null);
      setEndNumericValue(null);

      onValidated && onValidated(false);
    }
  }, [fieldSelectedItem]);

  useEffect(() => {
    // Set OPERATOR when one is selected from the list
    let operator: string | null = null;
    if (operatorSelectedItem && operatorSelectedItem.value) {
      operator = operatorSelectedItem.value.toString();
      setCurrentOperator(operator);

      setTextAreaValue('');
      setStartNumericValue(null);
      setEndNumericValue(null);

      onValidated && onValidated(false);
    }
  }, [operatorSelectedItem]);

  useEffect(() => {
    // Set VALUES to TEXT input
    if (currentField) {
      setCurrentValues([textAreaValue]);
      onValidated && onValidated(textAreaValue ? true : false);
    }
  }, [textAreaValue]);

  useEffect(() => {
    // Set VALUES to NUMERIC input
    if (currentField) {
      const tempValues = [];
      if (startNumericValue) {
        tempValues[0] = startNumericValue.toString();
      }
      if (endNumericValue) {
        tempValues[1] = endNumericValue.toString();
      }
      setCurrentValues(tempValues);

      const hasStart = startNumericValue ? true : false;
      const hasEnd = endNumericValue ? true : false;
      onValidated && onValidated(hasStart || hasEnd);
    }
  }, [startNumericValue, endNumericValue]);

  useEffect(() => {
    // Call onCriteriaChaged anytime the FIELD, OPERATOR, or VALUES change
    if (currentField) {
      onCriteriaChaged &&
        onCriteriaChaged(
          currentProductions,
          currentStatus,
          currentField,
          currentOperator,
          currentValues
        );
    }
  }, [currentStatus, currentField, currentOperator, currentValues]);

  useEffect(() => {
    // Call onCriteriaChaged anytime a PRODUCTION is selected
    if (currentProductions && currentProductions.length > 0) {
      onValidated && onValidated(true);
      onCriteriaChaged &&
        onCriteriaChaged(
          currentProductions,
          currentStatus,
          currentField,
          currentOperator,
          currentValues
        );
    } else {
      onValidated && onValidated(false);
    }
  }, [currentProductions]);

  const loadProductionData = (
    data: IAdvancedSearchTabsDataResponse | undefined
  ) => {
    // Map API data
    let fields: Field[] = [];
    let items: ICheckboxListItem[] = [];
    if (data && data.workProductItemsDetails) {
      fields = [...data.workProductItemsDetails];
      items = data.workProductItemsDetails.map((value) => {
        return {value: false, label: value.text ?? ''};
      });
    }

    // Set default values and selections
    setProductions(fields);
    setProductionCheckboxItems(items);
  };

  const loadProductionFieldData = (
    data: IAdvancedSearchTabsDataResponse | undefined
  ) => {
    // Map API data
    let fields: Field[] = [];
    let items: ISelectItem[] = [];
    if (data && data.workProductItemsDetails) {
      fields = [...data.workProductItemsDetails];
      items = data.workProductItemsDetails.map((value) => {
        return {value: value.id, label: value.text};
      });
    }

    // Set default values and selections
    setProductionFields(fields);
    setCurrentField(null);
    setFieldSelectItems(items);
    setFieldSelectedItem(null);
  };

  const setOperators = (field: Field | null) => {
    if (!field) {
      return;
    }

    // 'IS' is present by default
    const operators = [{...isOperator}];

    // Add 'RANGE' and 'LIST' if appropriate
    if (field.isRange) {
      operators.push({...rangeOperator});
    }
    if (field.isList) {
      operators.push({...listOperator});
    }

    setOperatorSelectItems(operators);
    setOperatorSelectedItem(operators[0]); // default to 'IS'
  };

  const renderOperators = () => {
    let component = <></>;

    if (fieldSelectedItem) {
      component = (
        <ArrSingleSelectDropdown
          dropdownLabel={t(
            'sessionSearch.queryList.workProduct.productionsDrawer.selectOperator'
          )}
          dropdownItemKey="value"
          dropdownItemDisplay="label"
          dropdownItems={operatorSelectItems}
          selectedValues={operatorSelectedItem}
          onSelectionChange={setOperatorSelectedItem}
          sizeVariant="large"
          dataTestId="asd-production-field-operator"
        />
      );
    }

    return component;
  };

  const renderValues = () => {
    let component = <></>;

    if (fieldSelectedItem) {
      switch (currentOperator) {
        case 'is':
          component = renderTextComponent();
          break;
        case 'range':
          component = renderRangeComponent();
          break;
        case 'list':
          component = renderTextComponent();
          break;
      }
    }

    return component;
  };

  const renderTextComponent = () => {
    return (
      <ArrTextArea
        value={textAreaValue}
        placeholder={t(
          'sessionSearch.queryList.workProduct.productionsDrawer.enterValue'
        )}
        rows={6}
        resizable={true}
        onChange={setTextAreaValue}
        dataTestId={`text-area-production-drawer`}
      />
    );
  };

  const renderRangeComponent = () => {
    return (
      <Stack spacing={2}>
        <Box className="range-start">
          <ArrNumericInput
            value={startNumericValue ?? undefined}
            label={t(
              'sessionSearch.queryList.workProduct.productionsDrawer.enterFromValue'
            )}
            onChange={setStartNumericValue}
            sizeVariant="large"
            dataTestId="number-field-start"
            showPercentage={false}
          />
        </Box>
        <Box className="range-end">
          <ArrNumericInput
            value={endNumericValue ?? undefined}
            label={t(
              'sessionSearch.queryList.workProduct.productionsDrawer.enterToValue'
            )}
            onChange={setEndNumericValue}
            sizeVariant="large"
            dataTestId="number-field-end"
            showPercentage={false}
          />
        </Box>
      </Stack>
    );
  };

  const renderHelpText = () => {
    let localizationKey = '';
    let id = ''; // <-- this is added for testing purposes

    if (fieldSelectedItem) {
      switch (currentOperator) {
        case 'is':
          id = 'is-help-text';
          localizationKey =
            'sessionSearch.queryList.workProduct.productionsDrawer.searchHelpContent.isHelp';
          break;
        case 'range':
          id = 'range-help-text';
          localizationKey =
            'sessionSearch.queryList.workProduct.productionsDrawer.searchHelpContent.rangeHelp';
          break;
        case 'list':
          id = 'list-help-text';
          localizationKey =
            'sessionSearch.queryList.workProduct.productionsDrawer.searchHelpContent.listHelp';
          break;
      }
    }

    return (
      <Typography
        id={id}
        className="typo-text body5_size"
        data-testid={`productions-field-type-${id}`}
        tabIndex={0}
      >
        <Trans i18nKey={localizationKey}></Trans>
      </Typography>
    );
  };

  const handleCheckboxListChange = (checked: boolean[]) => {
    // 'checked' array indices map to 'productions' indices
    const checkedFields: Field[] = [];
    for (let i = 0; i < checked.length; i++) {
      if (checked[i] === true) {
        checkedFields.push(productions[i]);
      }
    }

    setCurrentProductions(checkedFields);
  };

  return (
    <Grid container className="productions-panel">
      {/* Left Panel */}
      <Grid size={6} className="productions-panel_left-pane">
        <Stack spacing={2}>
          <Box>
            <Typography
              className="typo-text subhead4_size left-pane_header"
              data-testid={`productions-field-type-select-productions`}
              tabIndex={0}
            >
              {t(
                'sessionSearch.queryList.workProduct.productionsDrawer.selectProductions'
              )}
            </Typography>
          </Box>
          <Box>
            <Divider className="productions-divider" />
          </Box>
          <Box tabIndex={0}>
            <ArrCheckboxList
              childLabels={productionCheckboxItems}
              onChange={handleCheckboxListChange}
              showSelectAll={false}
              size="small"
              tabIndex={-1}
            />
          </Box>
          <Box>
            <ArrLoadingSpinner show={productionsPending} />
          </Box>
        </Stack>
      </Grid>
      {/* Right Panel */}
      <Grid size={6} className="productions-panel_right-pane">
        <Stack spacing={2}>
          <Box>
            <Typography
              className="typo-text subhead4_size right-pane_header"
              data-testid={`productions-field-type-optional-filters`}
              tabIndex={0}
            >
              {t(
                'sessionSearch.queryList.workProduct.productionsDrawer.optionalFilters'
              )}
            </Typography>
          </Box>
          <Box>
            <Divider className="productions-divider" />
          </Box>
          <Box>
            <ArrSingleSelectDropdown
              dropdownLabel={t(
                'sessionSearch.queryList.workProduct.productionsDrawer.selectStatus'
              )}
              dropdownItemKey="value"
              dropdownItemDisplay="label"
              dropdownItems={statusSelectItems}
              selectedValues={statusSelectedItem}
              onSelectionChange={setStatusSelectedItem}
              sizeVariant="large"
              dataTestId="asd-production-field-status"
            />
          </Box>
          <Box>
            <ArrSingleSelectDropdown
              dropdownLabel={t(
                'sessionSearch.queryList.workProduct.productionsDrawer.selectField'
              )}
              dropdownItemKey="value"
              dropdownItemDisplay="label"
              dropdownItems={fieldSelectItems}
              selectedValues={fieldSelectedItem}
              onSelectionChange={setFieldSelectedItem}
              sizeVariant="large"
              dataTestId="asd-production-field-selected-item"
              searchable
              showNoResultsText
            />
          </Box>
          <Box>{renderOperators()}</Box>
          <Box className="field-type-value">{renderValues()}</Box>
          <Box className="field-type-help">{renderHelpText()}</Box>
          <Box>
            <ArrLoadingSpinner show={productionFieldsPending} />
          </Box>
        </Stack>
      </Grid>
      {productionsFieldErred && showProductionFieldsToast && (
        <ArrToastNotification
          message={t(
            'sessionSearch.queryList.workProduct.productionsDrawer.productionFieldsError'
          )}
          status={[ToastType.Error]}
          onClose={() => setShowProductionFieldsToast(false)}
        />
      )}
      {productionsErred && showProductionsToast && (
        <ArrToastNotification
          message={t(
            'sessionSearch.queryList.workProduct.productionsDrawer.productionsError'
          )}
          status={[ToastType.Error]}
          onClose={() => setShowProductionsToast(false)}
        />
      )}
    </Grid>
  );
};

export default ProductionsFieldType;
